import { useEffect, useMemo, useState } from 'react'

import type { ErrorLike } from '@apollo/client'

import { useCallbackRef } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { useCommentsCounts } from './useCommentsCounts'
import type { CommentsCountFilters } from './useCommentsCounts'
import type { CommentStatus } from '../../graphqlEnums'
import type { FilterStatus } from '../../CommunityFilters/types'
import { useStableArray } from '../../util/useStableArray'

export const UseChannelCommentCounts_Channel = graphql(`
  fragment UseChannelCommentCounts_Channel on Channel @_unmask {
    id
    allowedActions
  }
`)

export type CommentCountsValues = {
  channelId: string | null
  count: number
  limit: number | null
}

type ApolloCountsState = {
  counts: CommentCountsValues[]
  loading: boolean
  error: ErrorLike | null
}

type UseCommentCountResult = {
  counts: CommentCountsValues[] | null
  loading: boolean
  error: ErrorLike | null
}

/**
 * Props for fetching channel comment counts.
 * @param channels - Array of channels.
 * @param status - Optional array of status filters to apply.
 * @param dateStart - Optional start date for filtering.
 * @param dateEnd - Optional end date for filtering.
 */
type CountFilterStatus = Extract<CommentStatus, FilterStatus>

function isCommentStatus(status: FilterStatus): status is CountFilterStatus {
  return status !== 'all'
}

type ChannelCommentCountsProps = {
  channels: FragmentOf<typeof UseChannelCommentCounts_Channel>[]
  status?: FilterStatus[]
  dateStart?: string
  dateEnd?: string
  limit?: number
}

type PusherEventData = {
  channelId?: string
}

const commentsEventsStandard = [
  PusherEvent.COMMENT_DISMISSED,
  PusherEvent.COMMENT_REPLIED,
  PusherEvent.COMMENT_UNDISMISSED,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_POST,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_CHANNEL,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_ORGANIZATION,
  PusherEvent.DISMISS_ALL_COMMENTS_FOR_CHANNELS,
  PusherEvent.CHANNEL_BACKFILL_ERROR,
]

const commentsEventsAddingComments = [
  PusherEvent.COMMENT_INGESTED,
  PusherEvent.CHANNEL_BACKFILL_DONE,
]

/**
 * Hook to fetch the number of new comments for an array of channels.
 * A new comment is an unreplied comment that has been created in the last 24 hours.
 *
 * @param channels - The channels to fetch comments count for
 * @param status - Optional array of status filters to apply
 * @param dateStart - Optional start date for filtering
 * @param dateEnd - Optional end date for filtering
 * @returns The number of comments for the channels, or null if loading/error/unsupported
 *
 * @example
 * const { counts, loading, error } = useNewCommentsCounts({
 *   channels: mockChannels,
 *   status: ['new'],
 *   publishedAt: {
 *     start: '2024-01-01',
 *     end: '2024-01-31'
 *   }
 * })
 **/
export function useChannelCommentCounts({
  channels,
  status,
  dateStart,
  dateEnd,
  limit,
}: ChannelCommentCountsProps): UseCommentCountResult {
  const organizationId = useOrganizationId()
  const stableAllowedChannelIds = useStableArray(
    channels.filter((c) => canPerform(c, 'viewComments')).map((c) => c.id),
  )

  const statusKey = useMemo(() => {
    return status ? JSON.stringify(status) : []
  }, [status])

  const memoizedStatus = useMemo(
    () => status || [],
    // We use statusKey as a dependency instead of status directly to prevent unnecessary
    // re-renders when the status array reference changes but its contents remain the same.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusKey],
  )

  const [state, setState] = useState<ApolloCountsState>({
    counts: [],
    loading: true,
    error: null,
  })

  // Reset state when organization changes to clear stale data from previous org
  useEffect(() => {
    setState({
      counts: [],
      loading: true,
      error: null,
    })
  }, [organizationId])

  const { fetchCounts } = useCommentsCounts()

  const fetchAndUpdateCounts = useCallbackRef(
    async ({ channelIds }: { channelIds?: string[] }) => {
      if (!organizationId) {
        return
      }

      setState((prev) => ({ ...prev, loading: true }))

      const filters: CommentsCountFilters = {
        ...(channelIds && channelIds.length > 0 ? { channelIds } : {}),
        ...(memoizedStatus &&
          memoizedStatus.length > 0 &&
          !memoizedStatus.some(
            (candidateStatus) => candidateStatus === 'all',
          ) && {
            status: memoizedStatus.filter(isCommentStatus),
          }),
        publishedAt: {
          start: dateStart ?? undefined,
          end: dateEnd ?? undefined,
        },
      }

      try {
        const { data, error: queryError } = await fetchCounts({
          organizationId,
          filters,
          limit,
        })

        if (!data) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: queryError || null,
          }))
          return
        }

        // update the counts for the changed channelIds
        setState((prev) => {
          const countsMap = new Map(
            prev.counts.map((count) => [count.channelId, count]),
          )

          ;(data.commentsCounts?.counts ?? []).forEach((count) => {
            if (count.channelId) {
              countsMap.set(count.channelId, count)
            }
          })

          return {
            counts: Array.from(countsMap.values()),
            loading: false,
            error: queryError || null,
          }
        })
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error : new Error('Unknown error'),
        }))
      }
    },
  )

  useEffect(() => {
    // Fetch counts when page reloads and organizationId/channels become available
    // Does not refetch when filters change - those only update via Pusher events
    if (!organizationId) {
      return
    }

    // When no channels are viewable, skip the fetch to avoid the no-filter
    // fallback that would return counts for all org channels
    if (stableAllowedChannelIds.length === 0) {
      setState((prev) => ({ ...prev, loading: false }))
      return
    }

    void fetchAndUpdateCounts({ channelIds: stableAllowedChannelIds })
  }, [organizationId, stableAllowedChannelIds, fetchAndUpdateCounts])

  usePusherEvent(
    [...commentsEventsStandard],
    ({ channelId }: PusherEventData) => {
      if (channelId && !stableAllowedChannelIds.includes(channelId)) return
      void fetchAndUpdateCounts(channelId ? { channelIds: [channelId] } : {})
    },
  )

  usePusherEvent(
    commentsEventsAddingComments,
    ({ channelId }: PusherEventData) => {
      if (channelId && !stableAllowedChannelIds.includes(channelId)) return
      if (channelId && limit) {
        const currentCount = state.counts.find((c) => c.channelId === channelId)
        if (currentCount && currentCount.count >= limit - 1) return
      }
      void fetchAndUpdateCounts(channelId ? { channelIds: [channelId] } : {})
    },
  )

  return {
    counts: state.counts.length > 0 ? state.counts : null,
    loading: state.loading,
    error: state.error,
  }
}
